import { z } from 'zod';
import { uuidSchema } from './common.schema';

/**
 * How a POSITIVE/INDETERMINATE result on a mandatory serology test is treated.
 *
 *   HARD_STOP — disqualifying: the donor is rejected automatically.
 *   SOFT_STOP — tracked only: the result is recorded on the donor's profile for
 *               institutional awareness, but the donor remains eligible.
 *
 * Fixed per test (only CMV is SOFT_STOP — pasteurization destroys it; every
 * other mandatory test is HARD_STOP) — see screeningEvaluator.ts. NOT
 * hospital-configurable, so it has no field on the request schema below.
 */
export const BloodTestClassificationSchema = z.enum(['HARD_STOP', 'SOFT_STOP']);
export type BloodTestClassification = z.infer<typeof BloodTestClassificationSchema>;

export const SaveApprovalComplianceSettingsRequestSchema = z.object({
  tenant_id: uuidSchema,
  hospital_id: uuidSchema,
  created_by: uuidSchema,
  screening_validity_days: z.number().int().min(1),
  blood_test_hiv_validity_days: z.number().int().min(1),
  blood_test_hbsag_validity_days: z.number().int().min(1),
  blood_test_hcv_validity_days: z.number().int().min(1),
  blood_test_htlv_validity_days: z.number().int().min(1),
  blood_test_cmv_validity_days: z.number().int().min(1),
  blood_test_syphilis_validity_days: z.number().int().min(1),
  bottle_quantities: z
    .array(z.number().int().min(1))
    .min(1)
    .refine((vals) => new Set(vals).size === vals.length, {
      message: 'bottle_quantities must not contain duplicate values',
    }),
  advance_feed_preparation: z.enum(['12-hour', '24-hour']),
});
export type SaveApprovalComplianceSettingsRequest = z.infer<
  typeof SaveApprovalComplianceSettingsRequestSchema
>;

export const ApprovalComplianceSettingSchema = z.object({
  settingId: uuidSchema,
  tenantId: uuidSchema,
  scopeType: z.string(),
  scopeId: uuidSchema,
  settingCategory: z.string(),
  settingKey: z.string(),
  settingValue: z.string(),
  valueType: z.string(),
  createdBy: uuidSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ApprovalComplianceSetting = z.infer<typeof ApprovalComplianceSettingSchema>;
